import { nanoid } from 'nanoid';

import type { ChatApiResponse, ChatMessage, MessageBlock } from '@/wabunka-src/features/Chat/models/chatMessage';

// ── ヘルパー ──

/** /contact URLに experience クエリパラメータを付与する（既に含まれている場合はスキップ） */
const appendExperienceSlugToContactUrl = (url: string | undefined, slug: string): string | undefined => {
  if (!url || !url.startsWith('/contact') || url.includes('experience=')) return url;
  const separator = url.includes('?') ? '&' : '?';
  return `${url}${separator}experience=${slug}`;
};

// ── ファクトリ関数 ──

export const createChatMessage = (params: Pick<ChatMessage, 'role' | 'blocks' | 'includeInHistory'> & Partial<Pick<ChatMessage, 'rawContent'>>): ChatMessage => ({
  id: nanoid(),
  role: params.role,
  blocks: params.blocks,
  rawContent: params.rawContent,
  includeInHistory: params.includeInHistory,
  timestamp: Date.now(),
});

export const createUserMessage = (text: string): ChatMessage =>
  createChatMessage({
    role: 'user',
    blocks: [{ type: 'text', markdown: text }],
    rawContent: text,
    includeInHistory: true,
  });

/**
 * エラーメッセージを作成する（Contact問い合わせ誘導ナビゲーション付き）
 * AIが回答できない場合は常に人間への導線を提供する。
 *
 * `includeInHistory` は呼び出し側で明示する：
 *   - true  : バックエンド/LLMが生成した文言（scope:'error' レスポンス等）。LLMの会話履歴に載せる。
 *   - false : フロントUI由来の通信エラー文言。LLM視点では発話していないので履歴に載せない。
 */
export const createErrorMessage = (message: string, slug: string, includeInHistory: boolean): ChatMessage =>
  createChatMessage({
    role: 'assistant',
    blocks: [
      { type: 'error', message },
      { type: 'navigation', navType: 'contact', url: `/contact/?experience=${slug}`, label: 'Contact us' },
    ],
    rawContent: message,
    includeInHistory,
  });

/**
 * APIレスポンスからアシスタントメッセージを作成する。
 * この関数がバックエンドのレスポンス構造とフロントのUIブロック構造を繋ぐ唯一の変換レイヤー。
 *
 * ── 現在のブロック生成ロジック ──
 * ■ [scope] によって生成するブロックが変わる:
 *   scope: 'in'    → TextBlock (必須) + NavigationBlock (navigation フィールドがある場合のみ)
 *   scope: 'out'   → TextBlock のみ（'in' と同じ扱い。将来的に別UIにする余地あり）
 *   scope: 'error' → ErrorBlock + Contact誘導NavigationBlock（createErrorMessage経由）
 *
 * ■ [navigation] フィールドによって追加されるブロック:
 *   navigation あり → TextBlock の後ろに NavigationBlock を追加（/contact URLにはexperience slugを自動付与）
 *   navigation なし → TextBlock のみ
 *
 * ── 今後の拡張パターン例 ──
 * 1. scope:'out' を別UIで表示したい場合
 *    → scope === 'out' の分岐を追加し、専用の OutOfScopeBlock を作成する
 *      例: { type: 'outOfScope', message: response.answer }
 *
 * 2. バックエンドが新しいブロック型を返すようになった場合（例: 画像カード、タグ候補）
 *    → ChatApiResponse に新フィールドを追加し、ここで対応するブロックを push する
 *
 * NOTE: blocks 配列の順序がそのまま表示順になる（ChatMessageRenderer が上から順にレンダリング）
 */
export const createAssistantMessageFromApiResponse = (response: ChatApiResponse, slug: string): ChatMessage => {
  if (response.scope === 'error') {
    // バックエンドが生成したエラー応答。LLMから見れば自分が出した発話なので履歴に残す。
    return createErrorMessage(response.answer, slug, true);
  }

  const blocks: MessageBlock[] = [{ type: 'text', markdown: response.answer }];

  if (response.navigation) {
    blocks.push({
      type: 'navigation',
      navType: response.navigation.type,
      url: appendExperienceSlugToContactUrl(response.navigation.url, slug),
      label: response.navigation.label,
    });
  }

  return createChatMessage({
    role: 'assistant',
    blocks,
    rawContent: response.answer,
    includeInHistory: true,
  });
};

const WELCOME_TAGS = ['How much does it cost?', 'What dates are available?', 'How do I get there?', 'Tell me about this experience', 'Can children join?'];

/**
 * ウェルカムメッセージを作成する
 */
export const createWelcomeMessage = (): ChatMessage => {
  const blocks: MessageBlock[] = [
    { type: 'text', markdown: "Hello, I'm Wabunka AI Chat. How can I help you with this experience?" },
    { type: 'selectTags', tags: WELCOME_TAGS },
  ];

  return createChatMessage({
    role: 'assistant',
    blocks,
    includeInHistory: false,
  });
};

/**
 * ChatApiResponse のコンバーター（useFetchToAiApi 用）
 */
export const chatApiResponseConverter = (res: unknown): ChatApiResponse => {
  // APIレスポンスが { response: { answer, scope, navigation? } } でラップされている場合に対応
  const raw = res as { response?: ChatApiResponse } & ChatApiResponse;
  const data = raw.response ?? raw;
  return {
    answer: data.answer,
    navigation: data.navigation,
    scope: data.scope,
  };
};
